import { Service, inject, signal } from '@angular/core'
import { io, Socket } from 'socket.io-client'
import { environment } from '../../../environments/environment'
import { LoggerService } from '@seiri/shared/frontend'

const { apiHost } = environment

@Service()
export class WebsocketService {
  private readonly logger = inject(LoggerService).forContext('WebsocketService')
  private socket: Socket | null = null
  private readonly authenticated = signal(false)

  isConnected(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.socket && this.socket.connected) {
        resolve(true)
      } else {
        const checkInterval = window.setInterval(() => {
          if (this.socket?.connected) {
            window.clearInterval(checkInterval)
            return resolve(true)
          }
        }, 100)
      }
    })
  }

  isAuthenticated(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.authenticated()) {
        resolve(true)
      } else {
        const checkInterval = window.setInterval(() => {
          if (this.authenticated()) {
            window.clearInterval(checkInterval)
            return resolve(true)
          }
        }, 100)
      }
    })
  }

  connect(): void {
    this.logger.debug('connecting')
    if (!this.socket || !this.socket.connected) {
      this.socket = io(apiHost, {
        path: '/ws',
        transports: ['websocket'],
      })

      this.socket.on('connect', () => {
        this.logger.debug('connected', { socketId: this.socket?.id })
      })

      this.socket.on('disconnect', () => {
        this.logger.debug('disconnected')
      })

      this.socket.on('authenticate:success', () => {
        this.logger.debug('authenticated')
        this.authenticated.set(true)
      })
    }
  }

  async authenticate(token: string): Promise<void> {
    await this.isConnected()
    if (!this.socket) {
      throw new Error('WebSocket is not connected. Please call connect() first.')
    }
    this.socket.emit('authenticate', token)
  }

  on<T>(event: string, callback: (data: T) => void): void {
    if (!this.socket) {
      throw new Error('WebSocket is not connected. Please call connect() first.')
    }
    this.socket.on(event, callback)
  }

  emit<T>(event: string, payload?: T): void {
    if (!this.socket) {
      throw new Error('WebSocket is not connected. Please call connect() first.')
    }
    this.socket.emit(event, payload)
  }

  disconnect(): void {
    if (!this.socket) {
      throw new Error('WebSocket is not connected. Please call connect() first.')
    }
    this.socket.disconnect()
  }
}
